import React, { useCallback, useEffect, useState } from 'react';
import {
  approveLscJoinRequest,
  listLscJoinRequests,
  rejectLscJoinRequest,
  LscApiError,
} from '../api';
import {
  formatJoinRequestDate,
  formatJoinRequestDiscord,
  formatJoinRequestUser,
  joinRequestActionsDisabled,
  shortenInviteCode,
} from '../joinRequestQueue';
import type { LscJoinRequest } from '../types';
import ErrorBanner from './ErrorBanner';

interface JoinRequestQueuePanelProps {
  leagueId: string;
  leagueArchived?: boolean;
  onDecision?: () => void;
  /** Skip the empty panel. Approve and reject stay available when a pending row exists. */
  hideWhenEmpty?: boolean;
}

export interface JoinRequestQueueActionsProps {
  requestId: string;
  leagueArchived: boolean;
  actingId: string | null;
  onApprove: (requestId: string) => void;
  onReject: (requestId: string) => void;
}

export function JoinRequestQueueActions({
  requestId,
  leagueArchived,
  actingId,
  onApprove,
  onReject,
}: JoinRequestQueueActionsProps) {
  const disabled = joinRequestActionsDisabled(leagueArchived, actingId, requestId);
  const working = actingId === requestId;

  return (
    <div className="flex shrink-0 gap-2" data-testid="join-request-queue-actions">
      <button
        type="button"
        className="btn-primary text-xs"
        disabled={disabled}
        onClick={() => onApprove(requestId)}
        data-testid="approve-join-request-button"
      >
        {working ? 'Working…' : 'Approve'}
      </button>
      <button
        type="button"
        className="btn-secondary text-xs"
        disabled={disabled}
        onClick={() => onReject(requestId)}
        data-testid="reject-join-request-button"
      >
        Reject
      </button>
    </div>
  );
}

const JoinRequestQueuePanel: React.FC<JoinRequestQueuePanelProps> = ({
  leagueId,
  leagueArchived = false,
  onDecision,
  hideWhenEmpty = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<LscJoinRequest[]>([]);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listLscJoinRequests(leagueId, { include_decided: false });
      setRequests(data.requests || []);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not load join requests.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const handleApprove = async (requestId: string) => {
    setActingId(requestId);
    setError(null);
    try {
      await approveLscJoinRequest(leagueId, requestId);
      await loadRequests();
      onDecision?.();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not approve join request.');
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    setActingId(requestId);
    setError(null);
    try {
      await rejectLscJoinRequest(leagueId, requestId, 'Rejected by commissioner');
      await loadRequests();
      onDecision?.();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not reject join request.');
    } finally {
      setActingId(null);
    }
  };

  const pendingRequests = requests.filter((item) => item.status === 'pending');

  if (hideWhenEmpty && (loading || (!error && pendingRequests.length === 0))) {
    return null;
  }

  return (
    <div className="card space-y-4" data-testid="join-request-queue-panel">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Pending join requests</h2>
        <p className="text-sm text-secondary-400">
          Review public or manual join requests before users become active league members.
        </p>
      </div>

      {leagueArchived && (
        <p className="text-sm text-secondary-500" data-testid="join-requests-archived-note">
          Approvals and rejections are disabled while the league is archived.
        </p>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <p className="text-sm text-secondary-400">Loading join requests…</p>
      ) : pendingRequests.length === 0 ? (
        <p className="text-sm text-secondary-500" data-testid="join-request-queue-empty">
          No pending join requests.
        </p>
      ) : (
        <div className="space-y-3" data-testid="join-request-queue-list">
          {pendingRequests.map((item) => {
            const requestId = item.id || item.request_id || '';
            return (
              <div
                key={requestId}
                className="rounded-lg border border-secondary-700 p-3"
                data-testid="join-request-queue-card"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1 space-y-1 text-sm">
                    <div className="font-medium text-white truncate">
                      {formatJoinRequestUser(item)}
                    </div>
                    <div className="text-secondary-400 truncate">
                      Discord: {formatJoinRequestDiscord(item)}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-secondary-500">
                      <span>
                        Invite:{' '}
                        <span className="font-mono">{shortenInviteCode(item.invite_code)}</span>
                      </span>
                      <span>Requested: {formatJoinRequestDate(item.created_at)}</span>
                      <span className="capitalize">Role: {item.requested_role ?? 'member'}</span>
                    </div>
                  </div>
                  <JoinRequestQueueActions
                    requestId={requestId}
                    leagueArchived={leagueArchived}
                    actingId={actingId}
                    onApprove={(id) => void handleApprove(id)}
                    onReject={(id) => void handleReject(id)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default JoinRequestQueuePanel;
